/**
 * 共享类型定义
 * 前台和后台共用的核心类型定义，确保数据一致性
 */

// ============ 职位类型枚举 ============

/**
 * 职位类型 - 统一前后台定义（大写下划线格式）
 */
export type JobType =
  | 'FULL_TIME'
  | 'PART_TIME'
  | 'INTERNSHIP_ONLINE'
  | 'INTERNSHIP_OFFLINE'
  | 'REMOTE'
  | 'REMOTE_GLOBAL';

/**
 * 职位类型数组 - 用于验证和下拉选项
 */
export const JOB_TYPES: JobType[] = [
  'FULL_TIME',
  'PART_TIME',
  'INTERNSHIP_ONLINE',
  'INTERNSHIP_OFFLINE',
  'REMOTE',
  'REMOTE_GLOBAL',
];

/**
 * 职位类型翻译键映射
 */
export type JobTypeKey =
  | 'fullTime'
  | 'partTime'
  | 'internshipOnline'
  | 'internshipOffline'
  | 'remote'
  | 'remoteGlobal';

export const JOB_TYPE_KEY_MAP: Record<JobType, JobTypeKey> = {
  'FULL_TIME': 'fullTime',
  'PART_TIME': 'partTime',
  'INTERNSHIP_ONLINE': 'internshipOnline',
  'INTERNSHIP_OFFLINE': 'internshipOffline',
  'REMOTE': 'remote',
  'REMOTE_GLOBAL': 'remoteGlobal',
};

// ============ 工作模式枚举 ============

/**
 * 工作模式 - 统一前后台定义（大写格式）
 * ONSITE: 到岗办公
 * HOME_BASED: 居家办公（需在指定城市）
 * REMOTE: 远程办公（无地域限制）
 * HYBRID: 混合办公
 */
export type WorkStyle = 'ONSITE' | 'HOME_BASED' | 'REMOTE' | 'HYBRID';

/**
 * 工作模式数组 - 用于验证和下拉选项
 */
export const WORK_STYLES: WorkStyle[] = ['ONSITE', 'HOME_BASED', 'REMOTE', 'HYBRID'];

/**
 * 工作模式翻译键映射
 */
export type WorkStyleKey = 'onsite' | 'homeBased' | 'remote' | 'hybrid';

export const WORK_STYLE_KEY_MAP: Record<WorkStyle, WorkStyleKey> = {
  'ONSITE': 'onsite',
  'HOME_BASED': 'homeBased',
  'REMOTE': 'remote',
  'HYBRID': 'hybrid',
};

// ============ 向后兼容类型别名 ============

/**
 * @deprecated 使用 WorkStyle 替代
 */
export type LegacyWorkStyle = 'onsite' | 'home-based';

/**
 * 旧工作模式到新工作模式的映射
 */
export const LEGACY_WORK_STYLE_MAP: Record<LegacyWorkStyle, WorkStyle> = {
  'onsite': 'ONSITE',
  'home-based': 'HOME_BASED',
};

// ============ 职位优先级 ============

export type JobPriority = 'HIGH' | 'MEDIUM' | 'LOW';

export const JOB_PRIORITIES: JobPriority[] = ['HIGH', 'MEDIUM', 'LOW'];

// ============ 国家接口 ============

/**
 * 国家数据结构
 */
export interface Country {
  code: string;      // ISO 3166-1 alpha-2 国家代码，如 "CN", "US"
  nameZh: string;    // 中文名称
  nameEn: string;    // 英文名称
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * 国家代码到名称的映射（预置数据）
 */
export const COUNTRY_DATA: Country[] = [
  { code: 'CN', nameZh: '中国', nameEn: 'China' },
  { code: 'GB', nameZh: '英国', nameEn: 'United Kingdom' },
  { code: 'US', nameZh: '美国', nameEn: 'United States' },
  { code: 'IN', nameZh: '印度', nameEn: 'India' },
  { code: 'VN', nameZh: '越南', nameEn: 'Vietnam' },
  { code: 'MY', nameZh: '马来西亚', nameEn: 'Malaysia' },
  { code: 'SG', nameZh: '新加坡', nameEn: 'Singapore' },
  { code: 'TH', nameZh: '泰国', nameEn: 'Thailand' },
  { code: 'ID', nameZh: '印度尼西亚', nameEn: 'Indonesia' },
  { code: 'KR', nameZh: '韩国', nameEn: 'South Korea' },
];

/**
 * 旧国家名称到新国家代码的映射
 */
export const LEGACY_COUNTRY_MAP: Record<string, string> = {
  'China': 'CN',
  'UK': 'GB',
  'USA': 'US',
  'India': 'IN',
  'Vietnam': 'VN',
  'Malaysia': 'MY',
  'Singapore': 'SG',
  'Thailand': 'TH',
  'Indonesia': 'ID',
  'South Korea': 'KR',
};

// ============ 城市接口 ============

/**
 * 城市数据结构
 */
export interface City {
  id: string;           // 城市代码，如 "tj", "bj"
  name: string;         // 中文名称
  enName: string;       // 英文名称
  countryCode: string;  // 关联的国家代码
  country?: Country;    // 关联的国家对象（可选，用于 API 返回）
  image: string;        // 城市图片 URL
  jobCount?: number;    // 动态计算的职位数量
  isRemoteOnly?: boolean; // 是否为纯远程城市
  createdAt?: Date;
  updatedAt?: Date;
}

// ============ 福利待遇接口 ============

/**
 * 福利待遇（支持中英文）
 */
export interface Benefit {
  en: string;
  zh: string;
}

// ============ 职位接口 ============

/**
 * 职位数据结构
 */
export interface Job {
  id: string;
  numericId?: number;       // PHP 投递接口需要的数字 ID
  title: string;
  titleEn?: string;
  location: string;
  cityId: string;           // 主城市 ID（外键）
  city?: City;              // 关联的城市对象（可选）
  cities?: string[];        // 多城市职位（城市 ID 数组）
  countryCode: string;      // 国家代码（外键）
  country?: Country;        // 关联的国家对象（可选）
  department: string;
  type: JobType;
  workStyle: WorkStyle;
  postedAt?: string;        // 相对时间字符串，如 "1 day ago"
  postedDate?: Date;        // 日期对象
  priority?: JobPriority;
  isOpen?: boolean;
  description?: string[];
  requirements?: string[];
  benefits?: Benefit[] | string[];
  viewCount?: number;
  applicantCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * 原始职位数据类型（不包含 postedDate 字段）
 */
export type JobRaw = Omit<Job, 'postedDate'>;

// ============ 部门相关 ============

export type DepartmentKey =
  | 'businessDevelopment'
  | 'marketing'
  | 'sales'
  | 'engineering'
  | 'product'
  | 'data'
  | 'operations'
  | 'finance'
  | 'campusAmbassador'
  | 'hr';

export const DEPARTMENT_KEY_MAP: Record<string, DepartmentKey> = {
  'Business Development': 'businessDevelopment',
  'Marketing': 'marketing',
  'Sales': 'sales',
  'Engineering': 'engineering',
  'Product': 'product',
  'Data': 'data',
  'Operations': 'operations',
  'Finance': 'finance',
  'Campus Ambassador': 'campusAmbassador',
  'HR': 'hr',
};

// ============ 多语言标签 ============

/**
 * 多语言标签结构
 */
export interface I18nLabel {
  zh: string;
  en: string;
}

/**
 * 部门多语言映射
 */
export const DEPARTMENT_I18N: Record<string, I18nLabel> = {
  'Business Development': { zh: '商务拓展', en: 'Business Development' },
  'Campus Ambassador': { zh: '校园大使', en: 'Campus Ambassador' },
  'Data': { zh: '数据', en: 'Data' },
  'Engineering': { zh: '技术研发', en: 'Engineering' },
  'Finance': { zh: '财务', en: 'Finance' },
  'Human Resources': { zh: '人力资源', en: 'Human Resources' },
  'HR': { zh: '人力资源', en: 'Human Resources' },
  'Marketing': { zh: '市场', en: 'Marketing' },
  'Operations': { zh: '运营', en: 'Operations' },
  'Product': { zh: '产品', en: 'Product' },
  'Sales': { zh: '销售', en: 'Sales' },
};

/**
 * 城市多语言映射
 */
export const CITY_I18N: Record<string, I18nLabel> = {
  // 中国
  'tj': { zh: '天津', en: 'Tianjin' },
  'bj': { zh: '北京', en: 'Beijing' },
  'cd': { zh: '成都', en: 'Chengdu' },
  'cq': { zh: '重庆', en: 'Chongqing' },
  'zz': { zh: '郑州', en: 'Zhengzhou' },
  'wh': { zh: '武汉', en: 'Wuhan' },
  'xa': { zh: '西安', en: "Xi'an" },
  'sh': { zh: '上海', en: 'Shanghai' },
  'gz': { zh: '广州', en: 'Guangzhou' },
  'sz': { zh: '深圳', en: 'Shenzhen' },
  'jn': { zh: '济南', en: 'Jinan' },
  'qd': { zh: '青岛', en: 'Qingdao' },
  'nj': { zh: '南京', en: 'Nanjing' },
  'su': { zh: '苏州', en: 'Suzhou' },
  'hz': { zh: '杭州', en: 'Hangzhou' },
  'xm': { zh: '厦门', en: 'Xiamen' },
  'hf': { zh: '合肥', en: 'Hefei' },
  'cs': { zh: '长沙', en: 'Changsha' },
  'dl': { zh: '大连', en: 'Dalian' },
  'hk': { zh: '香港', en: 'Hong Kong' },
  'tp': { zh: '台北', en: 'Taipei' },
  // 英国
  'ldn': { zh: '伦敦', en: 'London' },
  'lon': { zh: '伦敦', en: 'London' },
  'mnc': { zh: '曼彻斯特', en: 'Manchester' },
  'man': { zh: '曼彻斯特', en: 'Manchester' },
  // 美国
  'nyc': { zh: '纽约', en: 'New York' },
  'bst': { zh: '波士顿', en: 'Boston' },
  'lax': { zh: '洛杉矶', en: 'Los Angeles' },
  // 印度
  'mum': { zh: '孟买', en: 'Mumbai' },
  'pun': { zh: '浦那', en: 'Pune' },
  // 东南亚
  'han': { zh: '河内', en: 'Hanoi' },
  'kul': { zh: '吉隆坡', en: 'Kuala Lumpur' },
  'kl': { zh: '吉隆坡', en: 'Kuala Lumpur' },
  'sg': { zh: '新加坡', en: 'Singapore' },
  'bgk': { zh: '曼谷', en: 'Bangkok' },
  'jk': { zh: '雅加达', en: 'Jakarta' },
  'sel': { zh: '首尔', en: 'Seoul' },
  // 澳大利亚
  'syd': { zh: '悉尼', en: 'Sydney' },
  'mel': { zh: '墨尔本', en: 'Melbourne' },
  // 爱尔兰
  'dub': { zh: '都柏林', en: 'Dublin' },
};

/**
 * 国家多语言映射
 */
export const COUNTRY_I18N: Record<string, I18nLabel> = {
  'China': { zh: '中国', en: 'China' },
  'CN': { zh: '中国', en: 'China' },
  'UK': { zh: '英国', en: 'UK' },
  'GB': { zh: '英国', en: 'UK' },
  'USA': { zh: '美国', en: 'USA' },
  'US': { zh: '美国', en: 'USA' },
  'India': { zh: '印度', en: 'India' },
  'IN': { zh: '印度', en: 'India' },
  'Vietnam': { zh: '越南', en: 'Vietnam' },
  'VN': { zh: '越南', en: 'Vietnam' },
  'Malaysia': { zh: '马来西亚', en: 'Malaysia' },
  'MY': { zh: '马来西亚', en: 'Malaysia' },
  'Singapore': { zh: '新加坡', en: 'Singapore' },
  'SG': { zh: '新加坡', en: 'Singapore' },
  'Thailand': { zh: '泰国', en: 'Thailand' },
  'TH': { zh: '泰国', en: 'Thailand' },
  'Indonesia': { zh: '印度尼西亚', en: 'Indonesia' },
  'ID': { zh: '印度尼西亚', en: 'Indonesia' },
  'South Korea': { zh: '韩国', en: 'South Korea' },
  'KR': { zh: '韩国', en: 'South Korea' },
  'Australia': { zh: '澳大利亚', en: 'Australia' },
  'AU': { zh: '澳大利亚', en: 'Australia' },
  'Ireland': { zh: '爱尔兰', en: 'Ireland' },
  'IE': { zh: '爱尔兰', en: 'Ireland' },
  '中国': { zh: '中国', en: 'China' },
  '马来西亚': { zh: '马来西亚', en: 'Malaysia' },
  '新加坡': { zh: '新加坡', en: 'Singapore' },
  '澳大利亚': { zh: '澳大利亚', en: 'Australia' },
  '英国': { zh: '英国', en: 'UK' },
  '爱尔兰': { zh: '爱尔兰', en: 'Ireland' },
  '印度': { zh: '印度', en: 'India' },
};

/**
 * 职位类型多语言映射
 */
export const JOB_TYPE_I18N: Record<string, I18nLabel> = {
  'FULL_TIME': { zh: '全职', en: 'Full-time' },
  'PART_TIME': { zh: '兼职', en: 'Part-time' },
  'INTERNSHIP_ONLINE': { zh: '线上实习', en: 'Internship (Online)' },
  'INTERNSHIP_OFFLINE': { zh: '线下实习', en: 'Internship (Offline)' },
  'REMOTE': { zh: '远程', en: 'Remote' },
  'REMOTE_GLOBAL': { zh: '远程（全球）', en: 'Remote (Global)' },
};

/**
 * 工作模式多语言映射
 */
export const WORK_STYLE_I18N: Record<string, I18nLabel> = {
  'ONSITE': { zh: '到岗', en: 'On-site' },
  'HOME_BASED': { zh: '居家办公', en: 'Home-based' },
  'REMOTE': { zh: '远程', en: 'Remote' },
  'HYBRID': { zh: '混合', en: 'Hybrid' },
};

/**
 * 获取多语言标签
 * @param map 映射表
 * @param key 键值
 * @param lang 语言 'zh' | 'en'
 * @returns 对应语言的标签，如果找不到则返回原始 key
 */
export function getI18nLabel(
  map: Record<string, I18nLabel>,
  key: string,
  lang: 'zh' | 'en' = 'zh'
): string {
  return map[key]?.[lang] || key;
}

// ============ 国际区号选择器 ============

/**
 * 国家区号数据结构
 * 用于电话号码输入时的国际区号选择
 */
export interface CountryCode {
  code: string;      // ISO 3166-1 alpha-2 国家代码
  dialCode: string;  // 国际电话区号，如 "+86", "+1"
  nameZh: string;    // 中文国家名称
  nameEn: string;    // 英文国家名称
}

// ============ 验证工具函数 ============

/**
 * 验证是否为有效的 JobType
 */
export function isValidJobType(value: string): value is JobType {
  return JOB_TYPES.includes(value as JobType);
}

/**
 * 验证是否为有效的 WorkStyle
 */
export function isValidWorkStyle(value: string): value is WorkStyle {
  return WORK_STYLES.includes(value as WorkStyle);
}

/**
 * 验证是否为有效的国家代码（ISO 3166-1 alpha-2）
 */
export function isValidCountryCode(code: string): boolean {
  return /^[A-Z]{2}$/.test(code);
}
